/**
 * Беседы через переводчика: выбор стенда, запись беседы, список без стенда.
 *
 * Беседа сохраняется всегда, стенд у неё необязателен: разговор часто
 * начинается раньше, чем заведён стенд. Такая беседа ждёт в «Беседах без
 * стенда», пока её не привяжут.
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { db, enqueue, type DigestItem, type LocalSupplier, type LocalTalk } from '../db';
import { apiFetch, syncNow } from '../sync';
import { whereIs } from '../lib/where';
import { langOf } from '../lib/interpret';
import { loadRows } from '../lib/catalog';
import { shortNo } from './Browse';
import { TalkTasks } from './Tasks';
import { ulid, CONFIDENCE_BY_SOURCE, type StoneRow } from '@core';

// ── общее ───────────────────────────────────────────────────────────────────

export async function linkTalk(id: string, supplierId: string | null) {
  await db.talks.update(id, { supplierId: supplierId ?? undefined });
  await enqueue('talk.link', { id, supplierId }, ulid());
  void syncNow();
}

export async function removeTalk(id: string) {
  await db.talks.delete(id);
  await enqueue('talk.remove', { id }, ulid());
  void syncNow();
}

export function talkMinutes(t: LocalTalk): number {
  const end = t.endedAt ?? t.lines[t.lines.length - 1]?.at ?? t.startedAt;
  return Math.max(1, Math.round((new Date(end).getTime() - new Date(t.startedAt).getTime()) / 60_000));
}

export function talkWhen(t: LocalTalk): string {
  const d = new Date(t.startedAt);
  const day = d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
  const time = d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  return `${day} · ${time} · ${talkMinutes(t)} мин`;
}

/** Первая фраза поставщика — по ней беседу узнают в списке. */
function talkPreview(t: LocalTalk): string {
  const line = t.lines.find((l) => l.who === 'them') ?? t.lines[0];
  return line?.text ?? 'без реплик';
}

// ── выжимка: добавить находку к стенду или к камню ─────────────────────────

const KIND_LABEL: Record<DigestItem['kind'], string> = {
  price: 'Цены', stone: 'Камни', terms: 'Условия', agreement: 'Договорённости',
  contact: 'Контакты', info: 'Прочее',
};
const KIND_ORDER: DigestItem['kind'][] = ['price', 'stone', 'terms', 'agreement', 'contact', 'info'];

/** Отметить, что находка добавлена, — здесь и на сервере для других устройств. */
async function markApplied(talk: LocalTalk, index: number, to: string) {
  if (!talk.digest) return;
  const items = talk.digest.items.map((it, i) =>
    i === index ? { ...it, applied: [...(it.applied ?? []), to] } : it);
  await db.talks.update(talk.id, { digest: { ...talk.digest, items } });
  await enqueue('talk.applied', { id: talk.id, item: index, to }, ulid());
}

/** К стенду — строкой в его записи, с датой и пометкой, что это из беседы. */
async function applyToStand(talk: LocalTalk, index: number, stand: LocalSupplier) {
  const item = talk.digest!.items[index];
  const day = new Date(talk.startedAt).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
  const notes = [stand.notes?.trim(), `${day}, из беседы: ${item.text}`].filter(Boolean).join('\n');
  await db.suppliers.update(stand.id, { notes });
  await enqueue('supplier.upsert', { id: stand.id, companyName: stand.companyName, notes }, ulid());
  await markApplied(talk, index, 'stand');
  void syncNow();
}

/**
 * К камню — фактами с источником «со слов поставщика» и ссылкой на беседу.
 * Цена за карат, за карат лотом и общая — разные поля: спутать их — ошибка в разы.
 */
async function applyToStone(talk: LocalTalk, index: number, stoneId: string) {
  const item = talk.digest!.items[index];
  const now = new Date().toISOString();
  const put: Array<{ attribute: string; valueText: string | null; valueNum: number | null; unit: string | null }> = [];
  if (item.price) {
    const attr = item.price.per === 'ct' ? 'asking_price_per_ct'
      : item.price.per === 'lot_ct' ? 'asking_price_per_ct_lot' : 'asking_price';
    put.push({ attribute: attr, valueText: String(item.price.amount), valueNum: item.price.amount, unit: null });
    if (item.price.currency) put.push({ attribute: 'currency', valueText: item.price.currency, valueNum: null, unit: null });
  }
  for (const [k, v] of Object.entries(item.stone ?? {})) {
    const num = typeof v === 'number' ? v : null;
    put.push({ attribute: k, valueText: String(v), valueNum: num, unit: k === 'weight_ct' ? 'ct' : null });
  }
  for (const f of put) {
    const fact = {
      id: ulid(), subjectType: 'stone', subjectId: stoneId, ...f,
      sourceType: 'supplier_claim', confidence: CONFIDENCE_BY_SOURCE.supplier_claim,
      sourceRef: { conversationId: talk.id, quote: item.quote ?? item.text }, needsReview: false,
      recordedAt: now,
    };
    await db.facts.put(fact as never);
    await enqueue('fact.add', fact, ulid());
  }
  await markApplied(talk, index, stoneId);
  void syncNow();
}

/** Камень этого стенда, к которому отнести цену. */
function StoneChoice({ supplierId, onPick, onCancel }: {
  supplierId: string; onPick: (id: string) => void; onCancel: () => void;
}) {
  const [rows, setRows] = useState<StoneRow[]>([]);
  useEffect(() => {
    void loadRows().then((all) => setRows(all.filter((r) => r.supplierId === supplierId)));
  }, [supplierId]);
  const describe = (r: StoneRow) => {
    const v = (k: string) => r.values.get(k)?.text ?? (r.values.get(k)?.num != null ? String(r.values.get(k)!.num) : '');
    return [v('stone_type') || v('variety'), v('weight_ct') && `${v('weight_ct')} ct`, v('origin')].filter(Boolean).join(' · ');
  };
  return (
    <div className="app">
      <div className="bar">
        <button className="crumb" onClick={onCancel}>‹ Назад</button>
        <h1 className="t">К какому камню?</h1>
      </div>
      <div className="scroll">
        {rows.length ? (
          <div className="list">
            {rows.map((r) => (
              <button key={r.id} className="row" onClick={() => onPick(r.id)}>
                <span className="grow">
                  <span className="nm">{shortNo(r.no)}</span>
                  <span className="sub">{describe(r) || (r.kind === 'parcel' ? 'партия' : 'камень')}</span>
                </span>
                <span className="chev">›</span>
              </button>
            ))}
          </div>
        ) : <div className="blankslate">У этого стенда пока нет снятых камней.</div>}
      </div>
    </div>
  );
}

function DigestView({ talk, stand, stoneNos, onPickStone, onChanged }: {
  talk: LocalTalk;
  stand: LocalSupplier | null;
  stoneNos: Map<string, string>;
  onPickStone: (index: number) => void;
  onChanged: () => void;
}) {
  const d = talk.digest!;
  const groups = KIND_ORDER
    .map((k) => ({ k, items: d.items.map((it, i) => ({ it, i })).filter(({ it }) => it.kind === k) }))
    .filter((g) => g.items.length);
  return (
    <>
      {d.summary && <div className="memo digest-summary">{d.summary}</div>}
      {groups.map((g) => (
        <Fragment key={g.k}>
          <div className="head"><h3>{KIND_LABEL[g.k]}</h3><span>{g.items.length}</span></div>
          <div className="list">
            {g.items.map(({ it, i }) => {
              const applied = it.applied ?? [];
              const toStand = applied.includes('stand');
              const toStones = applied.filter((a) => a !== 'stand');
              const canStone = Boolean(stand) && (Boolean(it.price) || Boolean(it.stone));
              return (
                <div className="row digest-item" key={i}>
                  <span className="grow">
                    <span className="nm">{it.text}</span>
                    {it.quote && <span className="sub digest-quote">«{it.quote}»</span>}
                    {(toStand || toStones.length > 0) && (
                      <span className="sub digest-done">
                        ✓ {[toStand && 'в записях стенда', ...toStones.map((id) => `камень ${stoneNos.get(id) ?? ''}`.trim())]
                          .filter(Boolean).join(', ')}
                      </span>
                    )}
                    {stand && (
                      <span className="digest-acts">
                        {!toStand && (
                          <button className="state-btn" onClick={async () => { await applyToStand(talk, i, stand); onChanged(); }}>
                            К стенду
                          </button>
                        )}
                        {canStone && (
                          <button className="state-btn" onClick={() => onPickStone(i)}>К камню</button>
                        )}
                      </span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </Fragment>
      ))}
      {!stand && d.items.length > 0 && (
        <p className="meta" style={{ marginTop: 10 }}>
          Привяжите беседу к стенду — тогда находки можно будет добавить в его записи и к камням.
        </p>
      )}
      {d.items.length === 0 && !d.summary && <div className="blankslate">Ничего существенного в беседе не нашлось.</div>}
    </>
  );
}

// ── выбор стенда ────────────────────────────────────────────────────────────

/**
 * С кем разговор. Первым — стенд, у которого сейчас идёт съёмка или который
 * открывали последним: чаще всего разговор именно с ним.
 */
export function StandPick({ exhibitionId, title, skipLabel, onPick, onSkip, onCancel }: {
  exhibitionId?: string;
  title: string;
  skipLabel: string;
  onPick: (supplier: LocalSupplier) => void;
  onSkip: () => void;
  onCancel: () => void;
}) {
  const [stands, setStands] = useState<LocalSupplier[]>([]);
  const [recentId, setRecentId] = useState<string | null>(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    void (async () => {
      const all = exhibitionId
        ? await db.suppliers.where('exhibitionId').equals(exhibitionId).toArray()
        : await db.suppliers.toArray();
      all.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
      // Заход к стенду за последние полчаса — это, скорее всего, он.
      const last = (await db.sessions.orderBy('startedAt').reverse().first()) ?? null;
      const fresh = last && Date.now() - new Date(last.startedAt).getTime() < 30 * 60_000
        && all.some((s) => s.id === last.supplierId) ? last.supplierId : null;
      setRecentId(fresh);
      setStands(fresh ? [all.find((s) => s.id === fresh)!, ...all.filter((s) => s.id !== fresh)] : all);
    })();
  }, [exhibitionId]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return stands;
    return stands.filter((s) => [s.companyName, s.companyNameLocal, s.booth, s.office, s.contactPerson]
      .some((v) => v?.toLowerCase().includes(needle)));
  }, [q, stands]);

  return (
    <div className="app talk-standpick">
      <div className="bar">
        <button className="crumb" onClick={onCancel}>‹ Назад</button>
        <h1 className="t">{title}</h1>
      </div>
      <div className="scroll">
        <button className="btn btn-secondary" style={{ width: '100%', marginBottom: 14 }} onClick={onSkip}>
          {skipLabel}
        </button>
        {stands.length > 6 && (
          <label className="field">
            <input type="search" value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Название, стенд, имя" />
          </label>
        )}
        {shown.length > 0 ? (
          <div className="list">
            {shown.map((s) => (
              <button key={s.id} className="row" onClick={() => onPick(s)}>
                <span className="grow">
                  <span className="nm">{s.companyName}</span>
                  <span className="sub">
                    {s.id === recentId ? 'сейчас снимаете · ' : ''}{whereIs(s) || s.city || ''}
                  </span>
                </span>
                <span className="chev">›</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="blankslate">
            {stands.length ? 'Ничего не нашлось.' : 'Стендов на этом месте пока нет — привяжете беседу, когда снимете визитку.'}
          </div>
        )}
      </div>
    </div>
  );
}

// ── одна беседа ─────────────────────────────────────────────────────────────

export function TalkView({ talkId, onBack }: { talkId: string; onBack: () => void }) {
  const [talk, setTalk] = useState<LocalTalk | null | undefined>(undefined);
  const [stand, setStand] = useState<LocalSupplier | null>(null);
  const [picking, setPicking] = useState(false);
  const [stoneFor, setStoneFor] = useState<number | null>(null);
  const [stoneNos, setStoneNos] = useState<Map<string, string>>(new Map());
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const [tick, setTick] = useState(0);

  async function load() {
    const t = (await db.talks.get(talkId)) ?? null;
    setTalk(t);
    setStand(t?.supplierId ? (await db.suppliers.get(t.supplierId)) ?? null : null);
    if (t?.supplierId) {
      const stones = await db.stones.where('supplierId').equals(t.supplierId).toArray();
      setStoneNos(new Map(stones.map((st) => [st.id, shortNo(st.publicNo ?? st.localNo)])));
    }
    setTick((n) => n + 1);
  }
  useEffect(() => { void load(); }, [talkId]);

  /** Разобрать сейчас, не дожидаясь фоновой очереди. */
  async function digestNow() {
    if (!talk) return;
    setBusy(true);
    setProblem('');
    try {
      await syncNow();
      const res = await apiFetch(`/api/talks/${talk.id}/digest`, { method: 'POST' });
      const d = (await res.json().catch(() => ({}))) as { digest?: LocalTalk['digest']; error?: string };
      if (res.status === 404) setProblem('Беседа ещё не доехала до сервера — попробуйте через минуту.');
      else if (!res.ok || !d.digest) setProblem(d.error ?? 'Разобрать не получилось.');
      else {
        await db.talks.update(talk.id, { digest: d.digest, digestAt: new Date().toISOString(), digestError: undefined });
        await syncNow();
      }
    } catch {
      setProblem('Нет связи с сервером.');
    } finally {
      setBusy(false);
      await load();
    }
  }

  if (talk === undefined) return <div className="app"><div className="blankslate">Загружаю…</div></div>;
  if (!talk) return <div className="app"><div className="blankslate">Беседа не найдена.</div></div>;

  if (stoneFor !== null && stand) {
    return (
      <StoneChoice
        supplierId={stand.id}
        onPick={async (id) => { await applyToStone(talk, stoneFor, id); setStoneFor(null); await load(); }}
        onCancel={() => setStoneFor(null)}
      />
    );
  }

  if (picking) {
    return (
      <StandPick
        exhibitionId={talk.exhibitionId}
        title="К какому стенду?"
        skipLabel={stand ? 'Отвязать от стенда' : 'Оставить без стенда'}
        onPick={async (s) => { await linkTalk(talk.id, s.id); setPicking(false); await load(); }}
        onSkip={async () => { if (stand) await linkTalk(talk.id, null); setPicking(false); await load(); }}
        onCancel={() => setPicking(false)}
      />
    );
  }

  const them = langOf(talk.theirsLang);
  let prevAt = 0;

  return (
    <div className="app">
      <div className="bar">
        <button className="crumb" onClick={onBack}>‹ Назад</button>
        <h1 className="t">{stand?.companyName ?? 'Беседа без стенда'}</h1>
        <p className="meta">{talkWhen(talk)} · {langOf(talk.mineLang).ru} ⇄ {them.ru}</p>
      </div>
      <div className="scroll">
        <div className="states" style={{ marginBottom: 10 }}>
          <button className="state-btn" onClick={() => setPicking(true)}>
            {stand ? 'Сменить стенд' : 'Привязать к стенду'}
          </button>
          <button className="state-btn" style={{ color: 'var(--alert)' }} onClick={async () => {
            if (!window.confirm('Убрать беседу? Неделю она полежит в корзине — вернуть можно.')) return;
            await removeTalk(talk.id);
            onBack();
          }}>Убрать</button>
        </div>
        {talk.digest ? (
          <DigestView talk={talk} stand={stand} stoneNos={stoneNos}
                      onPickStone={setStoneFor} onChanged={() => void load()} />
        ) : (
          <div className="memo">
            {talk.digestError
              ? <>Разобрать беседу не получилось: {talk.digestError}</>
              : <>Выжимка — цены, договорённости и задачи — появится сама через несколько минут после конца беседы.</>}
            <div style={{ marginTop: 10 }}>
              <button className="state-btn" disabled={busy || talk.lines.length === 0} onClick={() => void digestNow()}>
                {busy ? 'Разбираю…' : 'Разобрать сейчас'}
              </button>
            </div>
          </div>
        )}
        {problem && <div className="memo warn">{problem}</div>}
        <TalkTasks key={tick} conversationId={talk.id} />

        <div className="head"><h3>Беседа</h3><span>{talk.lines.length}</span></div>
        <p className="meta" style={{ marginBottom: 14 }}>
          Слева — слова поставщика в переводе. Справа — ваши слова так, как их прочитал поставщик.
        </p>
        {talk.lines.length === 0 && <div className="blankslate">Реплик нет.</div>}
        <div className="talklog">
          {talk.lines.map((l) => {
            const at = new Date(l.at).getTime();
            const stamp = at - prevAt > 2 * 60_000;
            prevAt = at;
            return (
              // Без обёртки: реплика — прямой потомок ленты, иначе не встанет вправо.
              <Fragment key={l.n}>
                {stamp && (
                  <div className="talklog-time">
                    {new Date(l.at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                )}
                <p className={`talklog-msg ${l.who}`} lang={l.lang}>{l.text}</p>
              </Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── списки ──────────────────────────────────────────────────────────────────

function TalkRows({ talks, onOpen }: { talks: LocalTalk[]; onOpen: (id: string) => void }) {
  return (
    <div className="list">
      {talks.map((t) => (
        <button key={t.id} className="row" onClick={() => onOpen(t.id)}>
          <span className="grow">
            <span className="nm">{talkPreview(t)}</span>
            <span className="sub">{talkWhen(t)}</span>
          </span>
          <span className="chev">›</span>
        </button>
      ))}
    </div>
  );
}

/** Беседы, которые ещё ни к кому не привязаны. */
export function UnlinkedTalks({ onBack, onOpen }: { onBack: () => void; onOpen: (id: string) => void }) {
  const [talks, setTalks] = useState<LocalTalk[]>([]);
  useEffect(() => {
    void (async () => {
      const all = await db.talks.filter((t) => !t.supplierId && t.lines.length > 0).toArray();
      setTalks(all.sort((a, b) => b.startedAt.localeCompare(a.startedAt)));
    })();
  }, []);
  return (
    <div className="app tabbed">
      <div className="bar">
        <button className="crumb" onClick={onBack}>‹ Назад</button>
        <h1 className="t">Беседы без стенда</h1>
      </div>
      <div className="scroll">
        {talks.length ? <TalkRows talks={talks} onOpen={onOpen} />
          : <div className="blankslate">Все беседы привязаны к стендам.</div>}
      </div>
    </div>
  );
}

/** Сколько бесед ждут привязки — для строки на главной. */
export function useUnlinkedTalks(): number {
  const [n, setN] = useState(0);
  useEffect(() => {
    void db.talks.filter((t) => !t.supplierId && t.lines.length > 0).count().then(setN);
  }, []);
  return n;
}

/** Раздел «Беседы» в карточке стенда. */
export function SupplierTalks({ supplierId, onOpen }: { supplierId: string; onOpen: (id: string) => void }) {
  const [talks, setTalks] = useState<LocalTalk[]>([]);
  useEffect(() => {
    void (async () => {
      const list = await db.talks.where('supplierId').equals(supplierId).toArray();
      setTalks(list.filter((t) => t.lines.length > 0).sort((a, b) => b.startedAt.localeCompare(a.startedAt)));
    })();
  }, [supplierId]);
  if (!talks.length) return null;
  return (
    <>
      <div className="head">
        <h3>Беседы</h3><span>{talks.length}</span>
      </div>
      <TalkRows talks={talks} onOpen={onOpen} />
    </>
  );
}
